/**
 * DataFlowGraph v4: Events and projections are large nodes.
 * Compute (Lambda, Bedrock, SQS, SNS) are small inline nodes.
 * Rules are NOT shown — just their effect (event → compute → egress).
 * Flows strictly downward.
 */
import React, { useMemo, useRef, useEffect } from 'react';
import ReactFlow, { Controls, Background, BackgroundVariant, Node, Edge, MarkerType, ReactFlowInstance } from 'reactflow';
import dagre from '@dagrejs/dagre';
import 'reactflow/dist/style.css';

export interface DataFlowRule {
  name: string;
  sourceEventType: string;
  ruleId?: string;
  target: { type?: string; arn?: string; storeName?: string; eventBusArn?: string; targetEventType?: string };
}

export interface DataFlowGraphHandle {
  centerOnNode: (nodeId: string) => void;
}

export interface DataFlowProps {
  schemas: string[];
  rules: DataFlowRule[];
  projectionConfig?: { reducerType: string; name: string };
  ingress?: { type: string; name: string; targetEventTypes?: string[] }[];
  onNodeClick?: (id: string) => void;
  highlightedNodeId?: string;
  onHandle?: (handle: DataFlowGraphHandle) => void;
}

const LG = { w: 180, h: 40 };
const SM = { w: 90, h: 26 };

function svcLabel(type: string, arn?: string): string {
  const t = type.toUpperCase();
  if (t === 'LAMBDA') { const n = (arn || '').split(':function:').pop() || (arn || '').split(':').pop() || ''; return `λ ${n.replace('eventstore-', '')}`.trim(); }
  if (t === 'BEDROCK') return 'Bedrock';
  if (t === 'SQS') return 'SQS';
  if (t === 'SNS') return 'SNS';
  if (t === 'EVENTSTORE') return 'ES';
  return t;
}

function svcColor(type: string): string {
  const t = type.toUpperCase();
  if (t === 'LAMBDA') return '#d45b07';
  if (t === 'BEDROCK') return '#0972d3';
  if (t === 'SQS' || t === 'SNS') return '#d6242d';
  if (t === 'EVENTSTORE') return '#ec7211';
  return '#7d8998';
}

export function DataFlowGraph({ schemas, rules, projectionConfig, ingress, onNodeClick, highlightedNodeId, onHandle }: DataFlowProps) {
  const isDark = (() => { const s = localStorage.getItem('eventstore-theme'); return s ? s === 'dark' : window.matchMedia('(prefers-color-scheme: dark)').matches; })();
  const rfInstance = useRef<ReactFlowInstance | null>(null);
  const pendingCenter = useRef<string | null>(null);

  const performCenter = (instance: ReactFlowInstance, node: Node) => {
    const w = parseFloat(String(node.style?.width)) || LG.w;
    const h = parseFloat(String(node.style?.height)) || LG.h;
    instance.setCenter(node.position.x + w / 2, node.position.y + h / 2, { zoom: 1.2, duration: 400 });
  };

  const { nodes, edges, height } = useMemo(() => {
    const g = new dagre.graphlib.Graph();
    g.setDefaultEdgeLabel(() => ({}));
    g.setGraph({ rankdir: 'TB', ranksep: 50, nodesep: 15 });

    const allNodes: Node[] = [];
    const allEdges: Edge[] = [];
    const added = new Set<string>();
    let ei = 0;

    const mkEdge = (s: string, t: string, color: string) => {
      g.setEdge(s, t);
      allEdges.push({ id: `e${ei++}`, source: s, target: t, style: { stroke: color, strokeWidth: 1.5 }, markerEnd: { type: MarkerType.ArrowClosed, width: 8, height: 8, color } });
    };

    const addEvent = (id: string, label: string) => {
      if (added.has(id)) return; added.add(id);
      g.setNode(id, { width: LG.w, height: LG.h });
      allNodes.push({ id, type: 'default', position: { x: 0, y: 0 }, draggable: false, data: { label },
        style: { width: LG.w, height: LG.h, borderRadius: 8, border: `2px solid #0972d3`, background: isDark ? '#152238' : '#f0f8ff', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', color: isDark ? '#e9ebed' : '#16191f' } });
    };

    const addCompute = (id: string, label: string, color: string) => {
      if (added.has(id)) return; added.add(id);
      g.setNode(id, { width: SM.w, height: SM.h });
      allNodes.push({ id, type: 'default', position: { x: 0, y: 0 }, draggable: false, data: { label },
        style: { width: SM.w, height: SM.h, borderRadius: 14, background: color, border: 'none', fontSize: 10, fontWeight: 700, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' } });
    };

    const addEgress = (id: string, label: string, color: string) => {
      if (added.has(id)) return; added.add(id);
      g.setNode(id, { width: LG.w - 20, height: LG.h - 4 });
      allNodes.push({ id, type: 'default', position: { x: 0, y: 0 }, draggable: false, data: { label: `↑ ${label}` },
        style: { width: LG.w - 20, height: LG.h - 4, borderRadius: 8, border: `2px dashed ${color}`, background: isDark ? '#1a1a22' : '#fefefe', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', color: isDark ? '#e9ebed' : '#16191f' } });
    };

    // Ingress
    (ingress || []).forEach(ing => {
      const id = `in:${ing.name}`;
      if (!added.has(id)) {
        added.add(id);
        g.setNode(id, { width: LG.w - 20, height: LG.h - 4 });
        allNodes.push({ id, type: 'default', position: { x: 0, y: 0 }, draggable: false, data: { label: `↓ ${ing.type} ${ing.name}` },
          style: { width: LG.w - 20, height: LG.h - 4, borderRadius: 8, border: `2px dashed #ec7211`, background: isDark ? '#221a0a' : '#fffaf5', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', color: isDark ? '#e9ebed' : '#16191f' } });
      }
      if (schemas.length) {
        const filtered = ing.targetEventTypes?.filter(et => schemas.includes(et));
        const targets = filtered && filtered.length > 0 ? filtered : [schemas[0]];
        targets.forEach(targetEvent => {
          const cmpId = `cmp:ingest:${ing.name}:${targetEvent}`;
          addCompute(cmpId, 'λ ingest', '#d45b07');
          mkEdge(id, cmpId, '#d45b07');
          mkEdge(cmpId, `ev:${targetEvent}`, '#d45b07');
        });
      }
    });

    // Events
    schemas.forEach(et => addEvent(`ev:${et}`, et));

    // Rules: event → small compute → egress (consolidated per target to avoid horizontal spread)
    const processedTargets = new Set<string>();
    rules.forEach(rule => {
      const srcId = `ev:${rule.sourceEventType}`;
      if (!added.has(srcId)) return;
      const type = (rule.target?.type || '').toUpperCase();

      // INTERNAL rules (view mode): direct event→event edge within the graph
      if (type === 'INTERNAL' && rule.target?.targetEventType) {
        const targetId = `ev:${rule.target.targetEventType}`;
        if (added.has(targetId)) {
          mkEdge(srcId, targetId, '#7d8998');
        }
        return;
      }

      let egressId: string;
      let egressLabel: string;
      let color: string;

      if (type === 'EVENTSTORE' && rule.target?.storeName) {
        egressId = `out:${rule.target.storeName}`;
        egressLabel = rule.target.storeName;
        color = svcColor(type);
      } else if (type === 'SQS' || type === 'SNS') {
        const name = (rule.target?.arn || '').split(':').pop() || type;
        egressId = `out:${type.toLowerCase()}:${name}`;
        egressLabel = `${type} ${name}`;
        color = svcColor(type);
      } else if (type === 'LAMBDA' || type === 'BEDROCK') {
        // Lambda/Bedrock are compute — just show as a pill target, not a dashed egress box
        const name = (rule.target?.arn || '').split(':function:').pop()?.split('/').pop() || (rule.target?.arn || '').split(':').pop() || type;
        const cmpId = `cmp:${rule.name}`;
        addCompute(cmpId, svcLabel(type, rule.target?.arn), color = svcColor(type));
        mkEdge(srcId, cmpId, color);
        return; // no egress node needed — Lambda IS the terminal compute
      } else return;

      // Only add egress node once
      if (!processedTargets.has(egressId)) {
        processedTargets.add(egressId);
        addEgress(egressId, egressLabel, color);
      }

      // One compute pill per rule, connecting source → compute → egress
      const cmpId = `cmp:${rule.name}`;
      addCompute(cmpId, svcLabel(type, rule.target?.arn), color);
      mkEdge(srcId, cmpId, color);
      mkEdge(cmpId, egressId, color);
    });

    // Projection
    if (projectionConfig && schemas.length) {
      const cmpId = `cmp:reducer`;
      const projId = `proj:${projectionConfig.name}`;
      addCompute(cmpId, projectionConfig.reducerType, '#037f0c');
      g.setNode(projId, { width: LG.w, height: LG.h });
      if (!added.has(projId)) { added.add(projId);
        allNodes.push({ id: projId, type: 'default', position: { x: 0, y: 0 }, draggable: false, data: { label: `⟨proj⟩ ${projectionConfig.name}` },
          style: { width: LG.w, height: LG.h, borderRadius: 8, border: '2px solid #037f0c', background: isDark ? '#1a2a1a' : '#f0fff0', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', color: isDark ? '#e9ebed' : '#16191f' } });
      }
      mkEdge(`ev:${schemas[schemas.length - 1]}`, cmpId, '#037f0c');
      mkEdge(cmpId, projId, '#037f0c');
    }

    // Force rank separation: ingress (top) → events (middle) → egress (bottom)
    // Dagre determines rank from edge topology alone. Without constraints,
    // event nodes with no inbound edges float up to the ingress row, and
    // egress nodes with short paths sit on the same row as events.

    const ingressIds = (ingress || []).map(ing => `in:${ing.name}`);
    const eventIds = schemas.map(et => `ev:${et}`);
    const egressIds = Array.from(processedTargets);

    // Force rank separation using invisible constraint edges.
    // These are added to the dagre graph (for layout) but NOT to allEdges (not rendered).
    if (ingressIds.length > 0 && eventIds.length > 0) {
      // Every event node must be at least 2 ranks below some ingress node.
      // Events with inbound from ingress compute already satisfy this naturally.
      // Orphan events (no inbound) need an explicit constraint.
      const eventsWithInbound = new Set<string>();
      (g.edges() || []).forEach(e => { if (e.w.startsWith('ev:')) eventsWithInbound.add(e.w); });
      eventIds.forEach(evId => {
        if (!eventsWithInbound.has(evId)) {
          g.setEdge(ingressIds[0], evId, { minlen: 2, weight: 1 });
        }
      });
    }

    // Every egress node must be at least 2 ranks below every event node.
    // Even if egress already has inbound edges (event → compute → egress = 2 hops),
    // other event nodes without outbound to egress could end up on the same rank.
    // Force ALL events to be above ALL egress via constraint edges.
    if (egressIds.length > 0 && eventIds.length > 0) {
      eventIds.forEach(evId => {
        egressIds.forEach(outId => {
          // Only add if no path already exists (avoid duplicate edges)
          if (!g.hasEdge(evId, outId)) {
            g.setEdge(evId, outId, { minlen: 2, weight: 1 });
          }
        });
      });
    }

    dagre.layout(g);
    allNodes.forEach(n => { const p = g.node(n.id); if (p) { const w = parseFloat(String(n.style?.width)) || LG.w; const h = parseFloat(String(n.style?.height)) || LG.h; n.position = { x: p.x - w / 2, y: p.y - h / 2 }; } });
    const bounds = g.graph();
    return { nodes: allNodes, edges: allEdges, height: Math.max((bounds?.height ?? 500) + 80, 400) };
  }, [schemas, rules, projectionConfig, ingress, isDark]);

  useEffect(() => {
    if (onHandle) {
      onHandle({
        centerOnNode(nodeId: string) {
          const node = nodes.find(n => n.id === nodeId);
          if (!node) return;
          if (!rfInstance.current) {
            pendingCenter.current = nodeId;
            return;
          }
          performCenter(rfInstance.current, node);
        },
      });
    }
  }, [nodes, onHandle]);

  const styledNodes = useMemo(() => {
    if (!highlightedNodeId) return nodes;
    return nodes.map(n => {
      if (n.id === highlightedNodeId) {
        return { ...n, style: { ...n.style, boxShadow: '0 0 0 3px #0972d3, 0 0 12px rgba(9,114,211,0.4)' } };
      }
      return n;
    });
  }, [nodes, highlightedNodeId]);

  return (
    <div style={{ width: '100%', height, background: isDark ? '#141a20' : '#fafafa', borderRadius: 8, border: `1px solid ${isDark ? '#414750' : '#e9ebed'}` }}>
      <ReactFlow
        nodes={styledNodes}
        edges={edges}
        nodesDraggable={false}
        nodesConnectable={false}
        fitView
        fitViewOptions={{ padding: 0.2, minZoom: 0.7 }}
        minZoom={0.3}
        maxZoom={2}
        proOptions={{ hideAttribution: true }}
        onNodeClick={(_, n) => onNodeClick?.(n.id)}
        onInit={(instance) => {
          rfInstance.current = instance;
          if (pendingCenter.current) {
            const node = nodes.find(n => n.id === pendingCenter.current);
            pendingCenter.current = null;
            if (node) performCenter(instance, node);
          }
        }}
      >
        <Background variant={BackgroundVariant.Dots} gap={16} size={1.5} color={isDark ? '#555' : '#aaa'} />
        <Controls showInteractive={false} position="bottom-left" />
      </ReactFlow>
    </div>
  );
}
